import { zodResolver } from "@hookform/resolvers/zod";
import { Stack } from "@mui/material";
import dayjs from "dayjs";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import Button from "../../../components/UI/Button";
import AutoComplete, {
  ISelectType,
} from "../../../components/UI/Inputs/AutoComplete";
import DateInput from "../../../components/UI/Inputs/DateInput";
import FormCheckBox from "../../../components/UI/Inputs/FormCheckBox";
import FormInput from "../../../components/UI/Inputs/FormInput";
import Loading from "../../../components/UI/Loading";
import useFetch from "../../../hooks/useFetch";
import { ITicketAutomationReceivedRule } from "../../../services/tasySchedMenu.service";
import SelectFamilyMember from "../../Schedule/Insurance/AddInsurance/SelectFamilyMember";
import { ScheduleInsuranceService } from "../../Schedule/Insurance/insurance.service";
import {
  addInsuranceSchema,
  TAddHealth,
} from "../../Schedule/Insurance/AddInsurance/schema";

interface IInsuranceFormProps {
  individual?: ISelectType;
  rule?: ITicketAutomationReceivedRule | null;
  establishmentCode?: string;
  showIndividualSelect?: boolean;
  requireRuleForCategory?: boolean;
  insuranceId?: string;
  initialInsurance?: any;
  disableInsurance?: boolean;
  onSuccess: (insurance?: any) => void;
}

const KEYS = addInsuranceSchema.keyof().Enum;

const InsuranceForm: React.FC<IInsuranceFormProps> = ({
  individual,
  rule,
  establishmentCode,
  showIndividualSelect = false,
  requireRuleForCategory = true,
  insuranceId,
  initialInsurance,
  disableInsurance = false,
  onSuccess,
}) => {
  const [insurances, setInsurances] = useState<ISelectType[]>([]);
  const [categories, setCategories] = useState<ISelectType[]>([]);
  const [loading, setLoading] = useState(false);

  const { t } = useTranslation();

  const methods = useForm<TAddHealth>({
    resolver: zodResolver(addInsuranceSchema),
    defaultValues: {
      individual,
      main: false,
    },
  });

  const selectedInsurance = useWatch({
    control: methods.control,
    name: "insurance",
  });

  const { sendRequest: getInsurances, loading: insurancesLoading } = useFetch(
    ScheduleInsuranceService.newScheduleAllTasy2,
  );

  const { sendRequest: getCategories } = useFetch(
    ScheduleInsuranceService.categoriesTasy2,
  );

  const { sendRequest: save } = useFetch(ScheduleInsuranceService.create);
  const { sendRequest: edit } = useFetch(ScheduleInsuranceService.edit);
  const { sendRequest: getById, loading: getLoading } = useFetch(
    ScheduleInsuranceService.getById,
  );

  const isEditMode = !!insuranceId;

  const requiresCategoryByRule = useMemo(() => {
    if (!selectedInsurance?.id || !rule) return false;

    return rule.ticketAutomationReceivedRuleInsuranceCategories.some(
      (category) =>
        category.enable &&
        String(category.insuranceExternalId).trim() ===
          String(selectedInsurance.id).trim(),
    );
  }, [rule, selectedInsurance]);

  const requiresCategory = useMemo(() => {
    if (requireRuleForCategory) return requiresCategoryByRule;

    return categories.length > 0;
  }, [categories.length, requireRuleForCategory, requiresCategoryByRule]);

  const fetchInsurances = useCallback(async () => {
    const { data, success } = await getInsurances({
      EstablishmentCode: establishmentCode,
    });

    if (data && success) {
      setInsurances(
        data.map((insurance) => ({
          id: String(insurance.identifier),
          name: insurance.insuranceName || "",
        })),
      );
    }
  }, [establishmentCode, getInsurances]);

  const fetchCategories = useCallback(async () => {
    if (
      !selectedInsurance?.id ||
      (requireRuleForCategory && !requiresCategoryByRule)
    ) {
      setCategories([]);
      methods.setValue("category", null);
      return;
    }

    const { data, success } = await getCategories({
      InsuranceIdentifier: Number(selectedInsurance.id),
    });

    if (data && success) {
      setCategories(
        data.map((category) => ({
          id: String(category.identifier),
          name: category.name || "",
        })),
      );
    }
  }, [
    getCategories,
    methods,
    requireRuleForCategory,
    requiresCategoryByRule,
    selectedInsurance,
  ]);

  const onSubmit = useCallback(
    async (values: TAddHealth) => {
      if (requiresCategory && !values.category?.id) {
        methods.setError("category", {
          message: `${t("page.schedules.newSchedule.category.required")}`,
        });
        return;
      }

      setLoading(true);
      const payload = {
        cardNumber: values.code || undefined,
        cardValidity: values.validate
          ? dayjs(values.validate).format("YYYY-MM-DD")
          : undefined,
        insuranceIntegrationId: values.insurance.id,
        insuranceName: values.insurance.name,
        categoryIntegrationId: values.category?.id,
        categoryName: values.category?.name,
        main: values.main,
        individualId: values.individual.id,
      };

      const { success } = isEditMode
        ? await edit({ id: insuranceId, data: payload })
        : await save(payload);

      setLoading(false);

      if (success) {
        onSuccess({
          ...initialInsurance,
          code: insuranceId || initialInsurance?.code,
          id: insuranceId || initialInsurance?.id,
          insuranceIntegrationId: values.insurance.id,
          insuranceName: values.insurance.name,
          categoryIntegrationId: values.category?.id,
          categoryName: values.category?.name,
          cardNumber: values.code,
          cardValidity: values.validate
            ? dayjs(values.validate).format("YYYY-MM-DD")
            : undefined,
          individual: values.individual,
          individualId: values.individual.id,
          main: values.main,
        });
      }
    },
    [
      edit,
      initialInsurance,
      insuranceId,
      isEditMode,
      methods,
      onSuccess,
      requiresCategory,
      save,
      t,
    ],
  );

  const fetchInsurance = useCallback(async () => {
    if (!insuranceId) return;

    const { data, success } = await getById(insuranceId);

    if (data && success) {
      methods.reset({
        code: data.cardNumber || "",
        insurance: {
          id: String(data.insuranceIntegrationId || data.code),
          name: data.insuranceName,
        },
        category: data.categoryIntegrationId
          ? {
              id: data.categoryIntegrationId,
              name: data.categoryName || "",
            }
          : null,
        individual: data.individual,
        main: data.main,
        validate: data.cardValidity
          ? dayjs(data.cardValidity).add(1, "day").toDate()
          : undefined,
      });
    }
  }, [getById, insuranceId, methods]);

  const fillInitialInsurance = useCallback(() => {
    if (!initialInsurance || insuranceId) return;

    const insuranceIntegrationId =
      initialInsurance.insuranceIntegrationId ||
      initialInsurance.insuranceCode ||
      initialInsurance.code ||
      initialInsurance.id;

    methods.reset({
      code: initialInsurance.cardNumber || "",
      insurance: {
        id: String(insuranceIntegrationId || ""),
        name: initialInsurance.insuranceName || initialInsurance.name,
      },
      category: initialInsurance.categoryIntegrationId
        ? {
            id: initialInsurance.categoryIntegrationId,
            name: initialInsurance.categoryName || "",
          }
        : null,
      individual:
        initialInsurance.individual ||
        individual ||
        (initialInsurance.individualId
          ? {
              id: initialInsurance.individualId,
              name: initialInsurance.individualName || "",
            }
          : undefined),
      main: initialInsurance.main || false,
      validate: initialInsurance.cardValidity
        ? dayjs(initialInsurance.cardValidity).add(1, "day").toDate()
        : undefined,
    });
  }, [individual, initialInsurance, insuranceId, methods]);

  useEffect(() => {
    fetchInsurances();
  }, [fetchInsurances]);

  useEffect(() => {
    fetchInsurance();
  }, [fetchInsurance]);

  useEffect(() => {
    fillInitialInsurance();
  }, [fillInitialInsurance]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  if (insurancesLoading || getLoading) {
    return <Loading />;
  }

  return (
    <FormProvider {...methods}>
      <Stack gap={2} sx={{ mt: 2 }}>
        <AutoComplete
          getOptionLabel={(option) => option.name}
          size="small"
          label={t("page.schedules.insurance.addInsurance.insurance.label")}
          name={KEYS.insurance}
          options={insurances}
          disable={isEditMode || disableInsurance}
        />
        {showIndividualSelect && <SelectFamilyMember />}
        {requiresCategory && (
          <AutoComplete
            getOptionLabel={(option) => option.name}
            size="small"
            label={t("page.schedules.newSchedule.category.label")}
            name={KEYS.category}
            options={categories}
          />
        )}
        <DateInput
          hasDefaultDate={false}
          label={t("page.schedules.insurance.addInsurance.validity.label")}
          name={KEYS.validate}
        />
        <FormInput
          size="small"
          fullWidth
          label={t("page.schedules.insurance.addInsurance.number.label")}
          name={KEYS.code}
        />
        <FormCheckBox
          name="main"
          label={`${t("page.schedules.insurance.addInsurance.main")}`}
        />
        <Button
          variant="contained"
          sx={{ alignSelf: "end" }}
          onClick={methods.handleSubmit(onSubmit)}
          loading={loading}
        >
          {isEditMode
            ? t("page.schedules.insurance.addInsurance.buttonEdit")
            : t("page.schedules.insurance.addInsurance.buttonSave")}
        </Button>
      </Stack>
    </FormProvider>
  );
};

export default memo(InsuranceForm);
